import { FilterTypes } from '@/constants/FilterTypes';
import type { TanStackColumns, FilterOption } from '@/components/UI/Table/TableComponent.model';
import type { SearchQuery } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import UserAvatar from '@/components/UI/UserAvatar/UserAvatar';
import type { Endpoints } from '@/generated/internal-api/Endpoints';
import type { AsyncExportState } from '@/model/AsyncExport.model';
import { buildAsyncExportFilterStrings } from '@/support/asyncExport';
import { DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils';
import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import {
  AUDIT_LOG_ENTITY_TYPES,
  AUDIT_LOG_OPERATIONS,
  type AuditLogEntityType,
  type AuditLogRow,
} from './AuditLog.model';

const PLACEHOLDER = '-';

const withPlaceholder = (value: string | null | undefined): string => (_isNotBlank(value) ? value : PLACEHOLDER);

const formatUtcTimestamp = (value: string): string =>
  DateUtils.renderDateTime(value, { timezone: 'UTC', format: DateTimeRenderFormat.ISODateTimeUTC });

interface AuditLogColumnsOptions {
  onOpenEntity?: (itemDeveloperId: string, entityType: AuditLogRow['type']) => void;
  /**
   * When set to `'entity'`, only the columns that are useful for a single-entity history are
   * visible by default (timestamp, user, action, before/after values). The entity-history modal
   * has no view selector — the user can't save a personal column set — so the default has to be
   * a reasonable narrow view rather than every column.
   */
  scope?: 'study' | 'entity';
}

const ENTITY_SCOPE_DEFAULT_VISIBLE_COLUMNS = new Set<string>([
  'created_at',
  'created_by',
  'action',
  'old_value',
  'new_value',
]);

export const auditLogColumns = ({
  onOpenEntity,
  scope = 'study',
}: AuditLogColumnsOptions = {}): TanStackColumns<AuditLogRow> => {
  const isVisibleByDefault = (columnId: string): boolean =>
    scope === 'entity' ? ENTITY_SCOPE_DEFAULT_VISIBLE_COLUMNS.has(columnId) : true;
  return studyScopeColumns(onOpenEntity).map((column) => ({
    ...column,
    isVisible: isVisibleByDefault(column.id),
  }));
};

const studyScopeColumns = (onOpenEntity: AuditLogColumnsOptions['onOpenEntity']): TanStackColumns<AuditLogRow> => [
  {
    id: 'created_at',
    header: 'Timestamp (UTC)',
    accessorKey: 'created_at',
    isVisible: true,
    size: 200,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => formatUtcTimestamp(original.created_at),
  },
  {
    id: 'item_id',
    header: 'Item ID',
    accessorKey: 'item_id',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => {
      if (_isNil(onOpenEntity)) {
        return original.item_id;
      }
      return (
        <button
          type="button"
          className="link-button link blue"
          onClick={() => onOpenEntity(original.item_developer_id, original.type)}
          data-testid="audit-log-row__open-entity"
        >
          {original.item_id}
        </button>
      );
    },
  },
  {
    id: 'created_by',
    header: 'User',
    accessorKey: 'created_by',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => {
      const user = original.created_by;
      if (_isNil(user)) {
        return PLACEHOLDER;
      }
      return <UserAvatar user={user} tooltip={user.name ?? user.email} />;
    },
  },
  {
    id: 'transaction_id',
    header: 'Transaction ID',
    accessorKey: 'transaction_id',
    isVisible: true,
    size: 255,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => original.transaction_id,
  },
  {
    id: 'event_description',
    header: 'Event',
    accessorKey: 'event_description',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => original.event_description,
  },
  {
    id: 'action',
    header: 'Action',
    accessorKey: 'action',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => withPlaceholder(original.action),
  },
  {
    id: 'old_value',
    header: 'Old value',
    accessorKey: 'old_value',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => withPlaceholder(original.old_value),
  },
  {
    id: 'new_value',
    header: 'New value',
    accessorKey: 'new_value',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => withPlaceholder(original.new_value),
  },
  {
    id: 'attribute',
    header: 'Item',
    accessorKey: 'attribute',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => withPlaceholder(original.attribute),
  },
  {
    id: 'item_developer_id',
    header: 'Item developer ID',
    accessorKey: 'item_developer_id',
    size: 255,
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => original.item_developer_id,
  },
  {
    id: 'type',
    header: 'Item type',
    accessorKey: 'type',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => original.type,
  },
  {
    id: 'parent_1_name',
    header: 'Parent item 1',
    accessorKey: 'parent_1_name',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => withPlaceholder(original.parent_1_name),
  },
  {
    id: 'parent_1_developer_id',
    header: 'Parent 1 developer ID',
    accessorKey: 'parent_1_developer_id',
    size: 255,
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) =>
      withPlaceholder(original.parent_1_developer_id),
  },
  {
    id: 'parent_1_type',
    header: 'Parent 1 type',
    accessorKey: 'parent_1_type',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) =>
      _notNil(original.parent_1_type) ? String(original.parent_1_type) : PLACEHOLDER,
  },
  {
    id: 'parent_2_name',
    header: 'Parent item 2',
    accessorKey: 'parent_2_name',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => withPlaceholder(original.parent_2_name),
  },
  {
    id: 'parent_2_developer_id',
    header: 'Parent 2 developer ID',
    accessorKey: 'parent_2_developer_id',
    size: 255,
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) =>
      withPlaceholder(original.parent_2_developer_id),
  },
  {
    id: 'parent_2_type',
    header: 'Parent 2 type',
    accessorKey: 'parent_2_type',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) =>
      _notNil(original.parent_2_type) ? String(original.parent_2_type) : PLACEHOLDER,
  },
  {
    id: 'comment',
    header: 'Comment',
    accessorKey: 'comment',
    size: 300,
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: AuditLogRow } }) => withPlaceholder(original.comment),
  },
];

interface AuditLogFilterAndSearchOptions {
  scope?: 'study' | 'entity';
}

export const auditLogSearchByOptions = ({
  scope = 'study',
}: AuditLogFilterAndSearchOptions = {}): Array<SearchQuery> => {
  const options: Array<SearchQuery> = [];
  if (scope !== 'entity') {
    options.push(
      { value: 'item_id', display: 'Item ID' },
      { value: 'item_developer_id', display: 'Item developer ID' }
    );
  }
  options.push(
    { value: 'parent_1_name', display: 'Parent 1 name' },
    { value: 'parent_1_developer_id', display: 'Parent 1 developer ID' },
    { value: 'parent_2_name', display: 'Parent 2 name' },
    { value: 'parent_2_developer_id', display: 'Parent 2 developer ID' },
    { value: 'comment', display: 'Comment' },
    { value: 'transaction_id', display: 'Transaction ID' }
  );
  return options;
};

const TEXT_CONTAINS_OPERATIONS = [
  { name: 'contains', value: FilterTypes.contains },
  { name: 'does not contain', value: FilterTypes.not_contains },
];

export const titleCaseEntityType = (entityType: AuditLogEntityType): string =>
  entityType
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

const entityTypeOptions = AUDIT_LOG_ENTITY_TYPES.map((type) => ({
  name: titleCaseEntityType(type),
  value: type,
}));

const operationOptions = AUDIT_LOG_OPERATIONS.map((op) => ({
  name: op.charAt(0) + op.slice(1).toLowerCase(),
  value: op,
}));

export const auditLogFilterOptions = ({
  scope = 'study',
}: AuditLogFilterAndSearchOptions = {}): Array<FilterOption> => {
  const options: Array<FilterOption> = [
    {
      value: 'created_at',
      name: 'Timestamp',
      operations: [{ name: FilterTypes.between_datetime, value: FilterTypes.between_datetime }],
    },
    {
      value: 'user_api_id',
      name: 'User',
      operations: [
        { name: 'is equal to', value: FilterTypes.eq, options: [] },
        { name: 'is not equal to', value: FilterTypes.ne, options: [] },
      ],
      fetchFromApi: {
        placeholder: 'Search by user name',
        url: 'users.list',
        tableFilterMenuProperty: 'api_id',
      },
    },
    {
      value: 'operation',
      name: 'Operation',
      operations: [
        { name: 'is equal to', value: FilterTypes.eq, options: operationOptions },
        { name: 'is not equal to', value: FilterTypes.ne, options: operationOptions },
      ],
    },
  ];
  if (scope !== 'entity') {
    options.push(
      {
        value: 'type',
        name: 'Item type',
        operations: [
          { name: 'is equal to', value: FilterTypes.eq, options: entityTypeOptions },
          { name: 'is not equal to', value: FilterTypes.ne, options: entityTypeOptions },
        ],
      },
      { value: 'item_id', name: 'Item ID', operations: TEXT_CONTAINS_OPERATIONS },
      { value: 'item_developer_id', name: 'Item developer ID', operations: TEXT_CONTAINS_OPERATIONS }
    );
  }
  options.push(
    { value: 'parent_1_name', name: 'Parent 1 name', operations: TEXT_CONTAINS_OPERATIONS },
    {
      value: 'parent_1_developer_id',
      name: 'Parent 1 developer ID',
      operations: TEXT_CONTAINS_OPERATIONS,
    },
    { value: 'parent_2_name', name: 'Parent 2 name', operations: TEXT_CONTAINS_OPERATIONS },
    {
      value: 'parent_2_developer_id',
      name: 'Parent 2 developer ID',
      operations: TEXT_CONTAINS_OPERATIONS,
    },
    { value: 'comment', name: 'Comment', operations: TEXT_CONTAINS_OPERATIONS },
    { value: 'transaction_id', name: 'Transaction ID', operations: TEXT_CONTAINS_OPERATIONS }
  );
  return options;
};

type AuditLogExportBody = Endpoints['POST /api/v1/studies/{studyApiId}/exports/audit-logs']['request'];
type AuditLogQueryByValue = NonNullable<AuditLogExportBody['query_by']>;

// Runtime allowlist derived from the UI's hand-maintained audit-log search-by option list. The
// UI list and the backend AuditLogQueryBy enum are kept in sync by convention — this narrowing
// guards against drift by nulling out any value not in the set rather than blindly casting a
// bogus string onto the wire. Widest possible set (study scope) so the entity-scope subset is
// covered too — its values are a strict subset.
const AUDIT_LOG_QUERY_BY_VALUES = new Set<string>(auditLogSearchByOptions().map((option) => option.value));

const narrowQueryBy = (raw: string | undefined | null): AuditLogQueryByValue | null =>
  _notNil(raw) && AUDIT_LOG_QUERY_BY_VALUES.has(raw) ? (raw as AuditLogQueryByValue) : null;

interface BuildAuditLogExportBodyOptions {
  route: AsyncExportState['route'];
  view: AsyncExportState['view'];
  /** Non-blank when the export is entity-scoped (opened from the entity-history modal). */
  itemDeveloperId?: string | null;
}

/**
 * Shared body builder for audit-log async exports. Both the study-level page and the
 * entity-history modal fire the same POST, so a single helper keeps future wire-shape fixes
 * (filter encoding, query_by handling, etc.) in one place. Mirrors the read-path semantics in
 * APITable.utils.ts: filters flow as an array of `field|op|value` chunks; query_by is only forwarded when
 * there's real search text — otherwise the APItable's default first-search-by-option would
 * silently narrow an unfiltered export.
 */
export const buildAuditLogExportBody = ({
  route,
  view,
  itemDeveloperId,
}: BuildAuditLogExportBodyOptions): AuditLogExportBody => {
  const noFilters = route.noFilters === true;
  const filters = buildAsyncExportFilterStrings({ route, view });
  const query = _isNotBlank(view.query) ? view.query.trim() : null;
  return {
    item_developer_id: _isNotBlank(itemDeveloperId) ? itemDeveloperId : null,
    filters,
    query: noFilters ? null : query,
    query_by: noFilters || query === null ? null : narrowQueryBy(view.queryBy),
  };
};
